import { useCallback, useEffect, useMemo, useState } from 'react';

// Per-event "pin to quick access" state for attendee row actions. A pinned action surfaces as an icon
// button beside the row's ⋮ menu (and keeps a filled-pin row inside the menu). Stored in localStorage so a
// manager's pin choices persist per show across reloads, and synced across tabs (the `storage` event) and
// across same-tab instances (a custom event) so every open row + the table's width calc stay in lockstep.
//
// Green-zone implementation of the TIC-303 attendee-actions "pin to quick access" model (the same feature
// landing on the legacy/Classic view via branch `feature/06_12-tic-303-attendee-actions-dropdown`). Kept
// zone-local so the green Attendee List never depends on the red-zone tree.

const STORAGE_PREFIX = 'tixologist:pinned-actions:event-';
const UPDATE_EVENT = 'tixologist:pinned-actions-updated';

/**
 * The attendee-action ids pinned by default — i.e. for a manager who has never touched the pins on this
 * show. Print is the most common door-side action, so it surfaces as a quick button out of the box; the
 * manager can still unpin it (their explicit choice then persists, defaults no longer apply). Passed by the
 * attendee callers (`AttendeeActions`, `PunchupResponsesTable`); the hook itself stays generic.
 */
export const ATTENDEE_PINNED_DEFAULTS: readonly string[] = ['print'];

/**
 * Max quick-access pins kept at once. Pinning one more than this evicts the OLDEST still-pinned action
 * (FIFO), so the quick-actions toolbar never grows past a glanceable handful. Display order is unaffected —
 * callers render the toolbar by filtering the menu actions on membership, so it always stays menu-ordered.
 */
export const MAX_PINNED_ACTIONS = 3;

const storageKey = (eventId?: string) => `${STORAGE_PREFIX}${eventId ?? 'global'}`;

// `defaults` apply only when this event has NO stored entry (a never-touched show). Once the manager pins or
// unpins anything, the stored set is authoritative — including an explicit empty `[]` (all defaults removed).
const readSet = (key: string, defaults: readonly string[]): Set<string> => {
	if (typeof window === 'undefined') return new Set(defaults);
	try {
		const raw = window.localStorage.getItem(key);
		if (raw === null) return new Set(defaults); // never set for this event → defaults
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return new Set(defaults);
		return new Set(parsed.filter((x): x is string => typeof x === 'string'));
	} catch {
		return new Set(defaults);
	}
};

const writeSet = (key: string, value: Set<string>) => {
	if (typeof window === 'undefined') return;
	try {
		window.localStorage.setItem(key, JSON.stringify(Array.from(value)));
		window.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail: { key } }));
	} catch {
		// localStorage unavailable — pin state degrades to per-component memory.
	}
};

export const usePinnedActions = (eventId?: string, defaultPinnedIds: readonly string[] = []) => {
	const key = storageKey(eventId);
	// Stabilise the defaults by CONTENT, not identity. A caller passing an inline `['print']` literal (or the
	// `= []` default for callers that omit the arg) hands a fresh array every render; keying the effects below
	// on its identity would churn them into an update loop. Derive the array from a JSON key (lossless for any
	// id) so its reference changes only when the ids actually do.
	const defaultsKey = JSON.stringify(defaultPinnedIds);
	const defaults = useMemo<readonly string[]>(() => JSON.parse(defaultsKey) as string[], [defaultsKey]);

	const [pinned, setPinned] = useState<Set<string>>(() => readSet(key, defaults));

	useEffect(() => {
		setPinned(readSet(key, defaults));
	}, [key, defaults]);

	useEffect(() => {
		const onStorage = (e: StorageEvent) => {
			if (e.key === key) setPinned(readSet(key, defaults));
		};
		const onLocal = (e: Event) => {
			const detail = (e as CustomEvent).detail;
			if (detail?.key === key) setPinned(readSet(key, defaults));
		};
		window.addEventListener('storage', onStorage);
		window.addEventListener(UPDATE_EVENT, onLocal);
		return () => {
			window.removeEventListener('storage', onStorage);
			window.removeEventListener(UPDATE_EVENT, onLocal);
		};
	}, [key, defaults]);

	const togglePin = useCallback(
		(actionId: string) => {
			const next = new Set(readSet(key, defaults));
			if (next.has(actionId)) {
				next.delete(actionId);
			} else {
				next.add(actionId);
				// Cap the pinned set: pinning beyond MAX_PINNED_ACTIONS evicts the OLDEST still-pinned action
				// (FIFO — a Set preserves insertion order, so the first entry is the oldest). The toolbar's
				// render order is unaffected (callers filter the menu actions on membership → menu order).
				while (next.size > MAX_PINNED_ACTIONS) {
					const oldest = next.values().next().value;
					if (oldest === undefined) break;
					next.delete(oldest);
				}
			}
			writeSet(key, next);
			setPinned(next);
		},
		[key, defaults]
	);

	const isPinned = useCallback((actionId: string) => pinned.has(actionId), [pinned]);

	return { pinnedIds: pinned, isPinned, togglePin };
};
